/**
 * Pyramiden-Einstieg — gestaffelte Tranchen innerhalb EINER Kaufzone
 * (20.09.2026, Ronni: Vorbild „More Crypto Online" / HKCM).
 *
 * IDEE: Statt der ganzen Position auf einmal mehrere Teilkäufe innerhalb der
 * Kaufzone — der erste klein, wenn der Kurs das Fenster gerade erst berührt,
 * die tieferen größer, weil ihr Weg zum Stop kürzer und ihr Risiko pro Stück
 * entsprechend kleiner ist. Ronni: „Die größere Position, die ich unten am
 * Fenster angebe, die hat einen kürzeren Weg zum Stop. Und somit ist mein
 * Risiko besser eingeteilt."
 *
 * REGEL 1 — DIE PREISE KOMMEN AUS DER ECHTEN ZONE: `zoneOben`/`stop` sind
 * `limit_bis`/`ungueltig_unter` aus `setup-state.ts`, NICHT die Elliott-
 * Fibonacci-Linien aus `elliott.ts`. Elliott bleibt Kontext, kein Kaufweg —
 * eine Pyramide auf Elliott-Zonen wäre ein zweiter, unvalidierter Kaufweg
 * genau der Art, die dieses Projekt ausschliesst.
 *
 * REGEL 8 — KEIN HINTERTÜRCHEN INS RISIKOLIMIT: Das Gesamtrisiko über ALLE
 * Tranchen zusammen ist exakt das Budget, das ein einzelner Einstieg auch
 * bekommen hätte (`kontogroesse * risikoProzentGesamt / 100`), nur gleich-
 * mäßig auf `anzahlTranchen` verteilt. Wer denselben Trade in fünf Häppchen
 * zerlegt, darf dadurch nicht fünfmal so viel riskieren.
 *
 * KEINE ZWEITE STÜCKZAHL-FORMEL: Jede Tranche ist ein Aufruf von
 * `berechneStueckzahl()` aus `uebernahme.ts` — nur mit eigenem Teilbudget und
 * eigenem Einstiegspreis. Das ist dieselbe Rechnung, die auch der
 * Übernahme-Dialog für einen einzelnen Einstieg nutzt.
 */
import { berechneStueckzahl } from './uebernahme';

export interface PyramideEingabe {
  /** Obergrenze der echten Kaufzone (setup-state.ts: `limit_bis`). */
  zoneOben: number;
  /** Stop / Ungültig-Schwelle (`ungueltig_unter`) — gilt für ALLE Tranchen gleich. */
  stop: number;
  kontogroesse: number;
  /** Risiko GESAMT über alle Tranchen, in Prozent — dasselbe Feld wie bei einem Einzeleinstieg. */
  risikoProzentGesamt: number;
  fxRate: number;
  /** Wie viele Teilkäufe, typisch 3–5. */
  anzahlTranchen: number;
  bruchstueckeErlaubt?: boolean;
}

export interface Tranche {
  /** 1-basiert, oberste Tranche zuerst. */
  nummer: number;
  einstieg: number;
  /** 0 = oben an der Zone (erste Berührung), wächst Richtung Stop. */
  tiefeProzent: number;
  stueck: number;
  risikoBetrag: number;
  kapitalBindung: number;
  hinweis: string | null;
}

export interface Pyramide {
  tranchen: Tranche[];
  risikoJeTranchePct: number;
  gesamtStueck: number;
  gesamtKapital: number;
  gesamtRisiko: number;
}

/**
 * Mindestabstand der tiefsten Tranche zum Stop, als Anteil der Zonenbreite.
 * Ohne diese Grenze geht der Nenner (Einstieg − Stop) gegen null und die
 * Stückzahl explodiert — bei einer naiven 0–100%-Aufteilung liegt zwischen
 * Tranche 1 und der letzten Tranche gut das Achtfache an gebundenem Kapital,
 * obwohl beide dasselbe Risiko tragen. 12 % ist eine gesetzte, keine
 * gemessene Grenze (wie `RISIKO_STANDARD` in risiko.ts) — sie ändert kein
 * Signal, nur wie eng die letzte Tranche an den Stop heranrücken darf.
 */
const MIN_ABSTAND_ZUM_STOP_ANTEIL = 0.12;

/**
 * Baut den Tranchenplan. `null`, wenn die Eingabe nicht rechenbar ist —
 * dieselbe Ehrlichkeit wie `berechneStueckzahl`: lieber nichts zeigen als
 * eine Zahl aus ungültigen Werten.
 */
export function planePyramide(e: PyramideEingabe): Pyramide | null {
  if (
    !Number.isFinite(e.zoneOben) ||
    !Number.isFinite(e.stop) ||
    !(e.zoneOben > e.stop) ||
    !Number.isInteger(e.anzahlTranchen) ||
    e.anzahlTranchen < 1 ||
    !(e.kontogroesse > 0) ||
    !(e.risikoProzentGesamt > 0)
  ) {
    return null;
  }

  const breite = e.zoneOben - e.stop;
  const n = e.anzahlTranchen;
  const risikoJeTranchePct = e.risikoProzentGesamt / n;
  const maxTiefe = 1 - MIN_ABSTAND_ZUM_STOP_ANTEIL;

  const tranchen: Tranche[] = [];
  for (let i = 0; i < n; i++) {
    // Gleichmässig ueber die ERLAUBTE Tiefe verteilt, nie bis an den Stop.
    const tiefe = n === 1 ? 0 : (i / (n - 1)) * maxTiefe;
    const einstieg = e.zoneOben - tiefe * breite;
    const ergebnis = berechneStueckzahl({
      einstieg,
      stop: e.stop,
      kontogroesse: e.kontogroesse,
      risikoProzent: risikoJeTranchePct,
      fxRate: e.fxRate,
      bruchstueckeErlaubt: e.bruchstueckeErlaubt,
    });
    tranchen.push({
      nummer: i + 1,
      einstieg,
      tiefeProzent: tiefe,
      stueck: ergebnis.stueck,
      risikoBetrag: ergebnis.risikoBetrag,
      kapitalBindung: ergebnis.kapitalBindung,
      hinweis: ergebnis.hinweis,
    });
  }

  return {
    tranchen,
    risikoJeTranchePct,
    gesamtStueck: tranchen.reduce((s, t) => s + t.stueck, 0),
    gesamtKapital: tranchen.reduce((s, t) => s + t.kapitalBindung, 0),
    gesamtRisiko: tranchen.reduce((s, t) => s + t.risikoBetrag, 0),
  };
}

/**
 * Gewichteter Durchschnittspreis nach einer weiteren Tranche — dieselbe
 * Rechnung, die eine Nachkauf-Buchung auf eine bestehende Position anwendet
 * (siehe `nachkaufPosition` in queries.ts). Hier separat, rein und ungetestet
 * gegen die Datenbank, damit der Übernahme-Dialog den resultierenden
 * Durchschnittspreis VOR dem Buchen anzeigen kann.
 */
export function gewichteterEinstieg(
  bestehendeStueck: number,
  bestehenderEinstieg: number,
  neueStueck: number,
  neuerEinstieg: number
): number {
  const gesamtStueck = bestehendeStueck + neueStueck;
  if (gesamtStueck <= 0) return neuerEinstieg;
  return (bestehendeStueck * bestehenderEinstieg + neueStueck * neuerEinstieg) / gesamtStueck;
}
